// 3D scene rendered with react-three-fiber.
// Reuses the same game state (engine.js). 2D game coords -> 3D world:
//   worldX = (gameX - W/2) / 10
//   worldZ = (gameY - H/2) / 10  (ground plane is X-Z, Y is up)
// Player faces +X in game-space angle; in 3D we rotate by -angle around Y.

import React, { useRef, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { W, H } from "@/game/engine";
import { ghostPositionAt } from "@/lib/replay";

const S = 10; // scale divisor
export const worldX = (gx) => (gx - W / 2) / S;
export const worldZ = (gy) => (gy - H / 2) / S;
export const worldSize = { w: W / S, h: H / S };

// ---------- Ground ----------
function Ground({ level }) {
  const isOutdoor = level === "outdoor";
  const isNeonRain = level === "neon_rain";
  const isBeach = level === "beach";
  const color = isBeach ? "#d9b872" : isNeonRain ? "#2a1a3e" : isOutdoor ? "#4a4f57" : "#171a1f";
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[worldSize.w * 1.4, worldSize.h * 1.4]} />
      <meshStandardMaterial color={color} roughness={isNeonRain ? 0.4 : 0.95} metalness={isNeonRain ? 0.35 : 0.05} />
    </mesh>
  );
}

// ---------- Parking Lot Floor (bays w/ lines) ----------
function ParkingFloor({ state }) {
  const isOutdoor = state.level === "outdoor";
  const isBeach = state.level === "beach";
  const laneColor = isBeach ? "#ffffff" : isOutdoor ? "#e7ecf0" : "#606060";
  // Generate line meshes based on same layout as engine
  const bays = useMemo(() => {
    const carW = 30;
    const bayInnerGap = 22;
    const bayWidth = carW * 2 + bayInnerGap;
    const aisleWidth = isOutdoor ? 68 : 64;
    const sectionWidth = bayWidth + aisleWidth;
    const startX = 40;
    const total = Math.floor((W - startX * 2) / sectionWidth);
    const list = [];
    for (let b = 0; b < total; b++) {
      const bx = startX + b * sectionWidth;
      list.push({ x: bx, w: bayWidth });
    }
    return list;
  }, [isOutdoor]);

  return (
    <group>
      {/* Base lot floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} receiveShadow>
        <planeGeometry args={[worldSize.w, worldSize.h]} />
        <meshStandardMaterial color={isBeach ? "#e8c98a" : isOutdoor ? "#3a3f47" : "#0f1114"} roughness={1} />
      </mesh>
      {/* Bay boundary lines */}
      {bays.map((b, i) => (
        <group key={i}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[worldX(b.x - 3), 0.02, 0]}>
            <planeGeometry args={[0.2, (H - 60) / S]} />
            <meshStandardMaterial color={laneColor} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[worldX(b.x + b.w + 3), 0.02, 0]}>
            <planeGeometry args={[0.2, (H - 60) / S]} />
            <meshStandardMaterial color={laneColor} />
          </mesh>
          {/* Central dashed divider */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[worldX(b.x + b.w / 2), 0.02, 0]}>
            <planeGeometry args={[0.1, (H - 60) / S]} />
            <meshStandardMaterial color={laneColor} opacity={0.35} transparent />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// ---------- A parked (or enemy) car mesh ----------
function CarMesh({ x, y, w, h, color, angle = 0, isEnemy = false, headlights = false }) {
  const wx = worldX(x + w / 2);
  const wz = worldZ(y + h / 2);
  const ww = w / S;
  const wh = h / S;
  const bodyH = 1.1;

  return (
    <group position={[wx, 0, wz]} rotation={[0, -angle, 0]}>
      {/* Chassis (lower box) */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <boxGeometry args={[ww * 1.02, 0.35, wh * 1.02]} />
        <meshStandardMaterial color="#111" roughness={0.9} />
      </mesh>
      {/* Main body */}
      <mesh position={[0, bodyH / 2 + 0.35, 0]} castShadow>
        <boxGeometry args={[ww, bodyH, wh]} />
        <meshStandardMaterial color={color} roughness={0.6} metalness={0.35} />
      </mesh>
      {/* Cabin (smaller box on top) */}
      <mesh position={[0, bodyH + 0.35 + 0.35, 0]} castShadow>
        <boxGeometry args={[ww * 0.75, 0.7, wh * 0.55]} />
        <meshStandardMaterial color={isEnemy ? "#3a0011" : "#1a1a20"} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* Windshield reflection */}
      <mesh position={[0, bodyH + 0.35 + 0.36, wh * 0.28]}>
        <planeGeometry args={[ww * 0.7, 0.6]} />
        <meshStandardMaterial color="#88bbee" opacity={0.5} transparent metalness={0.9} roughness={0.1} />
      </mesh>
      {/* Wheels */}
      {[
        [-ww / 2 + 0.15, 0.2, wh / 2 - 0.2],
        [ ww / 2 - 0.15, 0.2, wh / 2 - 0.2],
        [-ww / 2 + 0.15, 0.2, -wh / 2 + 0.2],
        [ ww / 2 - 0.15, 0.2, -wh / 2 + 0.2],
      ].map((p, i) => (
        <mesh key={i} position={p} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.22, 0.22, 0.15, 12]} />
          <meshStandardMaterial color="#0a0a0a" roughness={0.9} />
        </mesh>
      ))}
      {/* Headlights (enemies always on, parked cars only if requested) */}
      {(isEnemy || headlights) && (
        <>
          <mesh position={[ww / 2 - 0.05, bodyH / 2 + 0.35, wh / 2 - 0.15]}>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshStandardMaterial color="#fff5c8" emissive="#fff5c8" emissiveIntensity={2} />
          </mesh>
          <mesh position={[ww / 2 - 0.05, bodyH / 2 + 0.35, -wh / 2 + 0.15]}>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshStandardMaterial color="#fff5c8" emissive="#fff5c8" emissiveIntensity={2} />
          </mesh>
          {isEnemy && (
            <spotLight
              position={[ww / 2, 0.9, 0]}
              angle={0.5}
              penumbra={0.4}
              intensity={2}
              distance={12}
              color="#fff2c0"
              target-position={[ww / 2 + 5, 0.6, 0]}
              castShadow={false}
            />
          )}
        </>
      )}
    </group>
  );
}

// ---------- Player (fugitive as humanoid boxes) ----------
function Fugitive({ state }) {
  const groupRef = useRef();
  const legLRef = useRef();
  const legRRef = useRef();
  const armLRef = useRef();
  const armRRef = useRef();
  const sparkGroupRef = useRef();
  const spark0Ref = useRef();
  const spark1Ref = useRef();
  const spark2Ref = useRef();

  useFrame((_, dt) => {
    const p = state.player;
    if (!groupRef.current) return;
    groupRef.current.position.x = worldX(p.x + p.w / 2);
    groupRef.current.position.z = worldZ(p.y + p.h / 2);
    groupRef.current.rotation.y = -p.angle - Math.PI / 2;

    // Walk cycle: sway legs/arms when moving (angle changes / velocity approx)
    const t = state.time * 8;
    const sway = Math.sin(t) * 0.5;
    if (legLRef.current) legLRef.current.rotation.x = sway;
    if (legRRef.current) legRRef.current.rotation.x = -sway;
    if (armLRef.current) armLRef.current.rotation.x = -sway * 0.7;
    if (armRRef.current) armRRef.current.rotation.x = sway * 0.7;

    // Prismatic sparks — 3 orbiting hue-cycled spheres
    if (state.player.handleGem === "prismatic" && sparkGroupRef.current) {
      sparkGroupRef.current.rotation.y = -groupRef.current.rotation.y + state.time * 2.2;
      const sparks = [spark0Ref.current, spark1Ref.current, spark2Ref.current];
      for (let i = 0; i < 3; i++) {
        const m = sparks[i];
        if (!m || !m.material) continue;
        const hue = ((state.time * 120 + i * 120) % 360) / 360;
        m.material.color.setHSL(hue, 1, 0.65);
        if (m.material.emissive) {
          m.material.emissive.setHSL(hue, 1, 0.55);
        }
      }
    }
  });

  const nowMs = state.time * 1000;
  const shielded = nowMs < state.player.invincibleUntil;
  const boosted = nowMs < state.player.speedBoostUntil;
  const skinColor = state.player.skinColor || "#00F0FF";
  const bodyColor = shielded ? "#39FF14" : skinColor;

  return (
    <group ref={groupRef} position={[0, 0, 0]}>
      {/* Legs */}
      <group ref={legLRef} position={[-0.18, 0.5, 0]}>
        <mesh castShadow position={[0, -0.25, 0]}>
          <boxGeometry args={[0.18, 0.5, 0.18]} />
          <meshStandardMaterial color="#222831" />
        </mesh>
      </group>
      <group ref={legRRef} position={[0.18, 0.5, 0]}>
        <mesh castShadow position={[0, -0.25, 0]}>
          <boxGeometry args={[0.18, 0.5, 0.18]} />
          <meshStandardMaterial color="#222831" />
        </mesh>
      </group>
      {/* Torso */}
      <mesh castShadow position={[0, 0.9, 0]}>
        <boxGeometry args={[0.55, 0.7, 0.35]} />
        <meshStandardMaterial color={bodyColor} emissive={bodyColor} emissiveIntensity={shielded ? 0.9 : 0.35} />
      </mesh>
      {/* Arms */}
      <group ref={armLRef} position={[-0.4, 1.1, 0]}>
        <mesh castShadow position={[0, -0.25, 0]}>
          <boxGeometry args={[0.15, 0.5, 0.15]} />
          <meshStandardMaterial color={bodyColor} emissive={bodyColor} emissiveIntensity={0.25} />
        </mesh>
      </group>
      <group ref={armRRef} position={[0.4, 1.1, 0]}>
        <mesh castShadow position={[0, -0.25, 0]}>
          <boxGeometry args={[0.15, 0.5, 0.15]} />
          <meshStandardMaterial color={bodyColor} emissive={bodyColor} emissiveIntensity={0.25} />
        </mesh>
      </group>
      {/* Head */}
      <mesh castShadow position={[0, 1.55, 0]}>
        <boxGeometry args={[0.35, 0.35, 0.35]} />
        <meshStandardMaterial color="#e0c9a4" />
      </mesh>
      {/* Face direction indicator (small nose) */}
      <mesh position={[0.19, 1.55, 0]}>
        <boxGeometry args={[0.08, 0.1, 0.1]} />
        <meshStandardMaterial color="#c4a17a" />
      </mesh>
      {/* Boost trail */}
      {boosted && (
        <mesh position={[-0.5, 0.9, 0]}>
          <sphereGeometry args={[0.3, 8, 8]} />
          <meshStandardMaterial color="#00F0FF" emissive="#00F0FF" emissiveIntensity={1.2} transparent opacity={0.5} />
        </mesh>
      )}
      {/* Shield bubble */}
      {shielded && (
        <mesh position={[0, 0.9, 0]}>
          <sphereGeometry args={[0.9, 16, 16]} />
          <meshStandardMaterial color="#39FF14" emissive="#39FF14" emissiveIntensity={0.6} transparent opacity={0.18} />
        </mesh>
      )}
      {/* Prismatic aura — 3 orbiting hue-cycling sparkles */}
      {state.player.handleGem === "prismatic" && (
        <group ref={sparkGroupRef} position={[0, 0.9, 0]}>
          <mesh ref={spark0Ref} position={[0.85, 0, 0]}>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial color="#B0F0FF" emissive="#B0F0FF" emissiveIntensity={1.6} transparent opacity={0.95} />
          </mesh>
          <mesh ref={spark1Ref} position={[-0.425, 0, 0.736]}>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial color="#B0F0FF" emissive="#B0F0FF" emissiveIntensity={1.6} transparent opacity={0.95} />
          </mesh>
          <mesh ref={spark2Ref} position={[-0.425, 0, -0.736]}>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial color="#B0F0FF" emissive="#B0F0FF" emissiveIntensity={1.6} transparent opacity={0.95} />
          </mesh>
          <pointLight position={[0, 0, 0]} color="#B0F0FF" intensity={0.6} distance={4} />
        </group>
      )}
    </group>
  );
}

// ---------- Exit (glowing green portal) ----------
function ExitBlock({ e }) {
  const isVertical = e.h > e.w; // side exits (right/left walls)
  const wx = worldX(e.x + e.w / 2);
  const wz = worldZ(e.y + e.h / 2);
  return (
    <group position={[wx, 0.05, wz]}>
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[e.w / S, e.h / S]} />
        <meshStandardMaterial color="#39FF14" emissive="#39FF14" emissiveIntensity={1.5} />
      </mesh>
      <mesh position={[0, 1.2, 0]}>
        <boxGeometry args={[Math.max(0.4, e.w / S), 2.4, Math.max(0.4, e.h / S)]} />
        <meshStandardMaterial color="#39FF14" emissive="#39FF14" emissiveIntensity={0.9} transparent opacity={0.25} />
      </mesh>
      <pointLight position={[0, 2, 0]} color="#39FF14" intensity={2} distance={10} />
    </group>
  );
}

// ---------- Power-up orb ----------
function PowerOrb({ pw, t }) {
  const wx = worldX(pw.x + pw.w / 2);
  const wz = worldZ(pw.y + pw.h / 2);
  const y = 0.9 + Math.sin(t * 3 + wx) * 0.15;
  const color = pw.kind === "shield" ? "#39FF14" : pw.kind === "speed" ? "#00F0FF" : "#FFD400";
  return (
    <group position={[wx, y, wz]}>
      <mesh>
        <sphereGeometry args={[0.35, 16, 16]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.4} />
      </mesh>
      <pointLight color={color} intensity={1.2} distance={4} />
    </group>
  );
}

// ---------- Walls (borders + pillars) ----------
function Walls({ walls, level }) {
  const isOutdoor = level === "outdoor";
  const isBeach = level === "beach";
  return walls.map((w, i) => {
    const wx = worldX(w.x + w.w / 2);
    const wz = worldZ(w.y + w.h / 2);
    // Palm tree obstacle on beach — trunk + spherical foliage
    if (w.palm) {
      return (
        <group key={i} position={[wx, 0, wz]}>
          <mesh position={[0, 1.6, 0]} castShadow>
            <cylinderGeometry args={[0.18, 0.24, 3.2, 8]} />
            <meshStandardMaterial color="#7a4a1e" roughness={0.9} />
          </mesh>
          <mesh position={[0, 3.4, 0]} castShadow>
            <sphereGeometry args={[0.9, 12, 10]} />
            <meshStandardMaterial color="#2e7a3a" roughness={0.7} />
          </mesh>
          <mesh position={[0, 3.6, 0]} castShadow>
            <sphereGeometry args={[0.35, 8, 8]} />
            <meshStandardMaterial color="#3a1e0a" roughness={0.6} />
          </mesh>
        </group>
      );
    }
    const isBorder = w.w > 40 || w.h > 40;
    const height = isBorder ? ((isOutdoor || isBeach) ? 0.8 : 3.5) : 4.5;
    const color = isBorder ? (isBeach ? "#c8a565" : isOutdoor ? "#5a6068" : "#1a1c22") : "#2a2c33";
    return (
      <mesh key={i} position={[wx, height / 2, wz]} castShadow receiveShadow>
        <boxGeometry args={[w.w / S, height, w.h / S]} />
        <meshStandardMaterial color={color} roughness={0.9} />
      </mesh>
    );
  });
}

// ---------- Ceiling for underground ----------
function Ceiling({ state }) {
  return (
    <group>
      {/* No solid ceiling mesh - it would block the chase camera. Just light fixtures + support beams. */}
      {[
        [-worldSize.w * 0.35, worldSize.h * 0.32],
        [-worldSize.w * 0.12, worldSize.h * 0.32],
        [worldSize.w * 0.12, worldSize.h * 0.32],
        [worldSize.w * 0.35, worldSize.h * 0.32],
        [-worldSize.w * 0.35, 0],
        [worldSize.w * 0.35, 0],
        [-worldSize.w * 0.35, -worldSize.h * 0.32],
        [-worldSize.w * 0.12, -worldSize.h * 0.32],
        [worldSize.w * 0.12, -worldSize.h * 0.32],
        [worldSize.w * 0.35, -worldSize.h * 0.32],
      ].map(([x, z], i) => (
        <group key={i} position={[x, 4.0, z]}>
          <mesh>
            <boxGeometry args={[1.6, 0.15, 0.5]} />
            <meshStandardMaterial color="#fff4c8" emissive="#fff4c8" emissiveIntensity={5} />
          </mesh>
          <pointLight color="#fff2c0" intensity={12} distance={32} decay={1.2} castShadow={false} />
        </group>
      ))}
      {/* Overhead structural beams (thin dark bars) for atmosphere */}
      {[-worldSize.h * 0.35, 0, worldSize.h * 0.35].map((z, i) => (
        <mesh key={`beam-${i}`} position={[0, 4.2, z]}>
          <boxGeometry args={[worldSize.w, 0.25, 0.6]} />
          <meshStandardMaterial color="#0d0f13" />
        </mesh>
      ))}
    </group>
  );
}

// ---------- Sky/Sun for outdoor ----------
function OutdoorSky({ sunset = false }) {
  return (
    <group>
      <mesh position={[worldSize.w * 0.4, sunset ? 6 : 12, -worldSize.h * 0.5]}>
        <sphereGeometry args={[sunset ? 3.5 : 2.5, 24, 24]} />
        <meshStandardMaterial
          color={sunset ? "#ff7040" : "#fff2b0"}
          emissive={sunset ? "#ff7040" : "#fff2b0"}
          emissiveIntensity={sunset ? 3.2 : 2.5}
        />
      </mesh>
    </group>
  );
}

// ---------- Camera Follow ----------
function ChaseCamera({ state }) {
  const { camera } = useThree();
  const target = useRef(new THREE.Vector3());
  const targetCam = useRef(new THREE.Vector3());
  const isOutdoor = state.level === "outdoor";

  useFrame(() => {
    const p = state.player;
    const px = worldX(p.x + p.w / 2);
    const pz = worldZ(p.y + p.h / 2);
    // Outdoor: high angle, wider view. Underground: lower (below ceiling), tighter.
    if (isOutdoor) {
      targetCam.current.set(px - 0.6, 12, pz + 13);
      target.current.set(px, 0.8, pz);
    } else {
      targetCam.current.set(px - 0.4, 3.4, pz + 8);
      target.current.set(px, 0.8, pz - 1.5);
    }
    camera.position.lerp(targetCam.current, 0.14);
    camera.lookAt(target.current);
  });

  return null;
}

// ---------- Player flashlight (underground only) ----------
function PlayerFlashlight({ state }) {
  const lightRef = useRef();
  useFrame(() => {
    if (!lightRef.current) return;
    const p = state.player;
    lightRef.current.position.x = worldX(p.x + p.w / 2);
    lightRef.current.position.z = worldZ(p.y + p.h / 2);
  });
  return (
    <pointLight
      ref={lightRef}
      position={[0, 1.6, 0]}
      color="#a8f0ff"
      intensity={7}
      distance={24}
      decay={1.2}
    />
  );
}

// ---------- Ghost player (daily replay) ----------
function GhostPlayer3D({ state }) {
  const groupRef = useRef();
  useFrame(() => {
    if (!groupRef.current || !state.ghostReplay) return;
    const g = ghostPositionAt(state.ghostReplay, state.time);
    if (!g) {
      groupRef.current.visible = false;
      return;
    }
    groupRef.current.visible = true;
    groupRef.current.position.x = worldX(g.x + 7);
    groupRef.current.position.z = worldZ(g.y + 7);
  });
  if (!state.ghostReplay || state.ghostReplay.length === 0) return null;
  return (
    <group ref={groupRef} position={[0, 0, 0]}>
      <mesh position={[0, 0.9, 0]}>
        <capsuleGeometry args={[0.35, 0.9, 6, 10]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={0.7}
          transparent
          opacity={0.35}
          depthWrite={false}
        />
      </mesh>
      <pointLight position={[0, 1.2, 0]} color="#ffffff" intensity={0.6} distance={4} decay={2} />
    </group>
  );
}

// ---------- Main Scene ----------
export default function Scene3D({ state }) {
  const isOutdoor = state.level === "outdoor";
  const isRooftop = state.level === "rooftop";
  const isHighway = state.level === "highway";
  const isSnow = state.level === "snowstorm";
  const isTunnels = state.level === "tunnels";
  const isNeonRain = state.level === "neon_rain";
  const isBeach = state.level === "beach";
  const isDaylight = isOutdoor || isRooftop || isSnow || isBeach;

  // Weather overlay (Season Pass): fog tightens the view, rain darkens palette.
  const weather = state.weather;
  const fogNear = weather && weather.type === "fog" ? 20 - 6 * weather.intensity : (isSnow ? 40 : (isDaylight ? 60 : 70));
  const fogFar = weather && weather.type === "fog" ? 55 - 15 * weather.intensity : (isSnow ? 100 : (isDaylight ? 140 : 140));

  const bgColor = isBeach ? "#f9d68e"
    : isSnow ? "#e8f0f8"
    : isRooftop ? "#f2794b"
    : isHighway ? "#0a1224"
    : isTunnels ? "#151820"
    : isNeonRain ? "#1a0a2e"
    : isOutdoor ? "#c5d5e0"
    : "#1a2030";
  const fogColor = isBeach ? "#f0c880"
    : isSnow ? "#d8e2ee"
    : isRooftop ? "#e07040"
    : isHighway ? "#0a1224"
    : isTunnels ? "#151820"
    : isNeonRain ? "#1a0a2e"
    : isOutdoor ? "#b4c8d6"
    : "#1a2030";

  return (
    <>
      <color attach="background" args={[bgColor]} />
      <fog attach="fog" args={[fogColor, fogNear, fogFar]} />

      {isDaylight ? (
        <>
          <ambientLight intensity={isBeach ? 1.2 : (isSnow ? 1.1 : 0.7)} color={isBeach ? "#ffe4b0" : isSnow ? "#e0eaf5" : isRooftop ? "#ffc899" : "#fff5e0"} />
          <directionalLight
            position={[20, 30, -15]}
            intensity={isBeach ? 2.1 : (isSnow ? 1.2 : 1.9)}
            color={isBeach ? "#ffd48a" : isSnow ? "#f0f4fa" : isRooftop ? "#ff9040" : "#fff2c8"}
            castShadow
            shadow-mapSize={[1024, 1024]}
            shadow-camera-left={-60}
            shadow-camera-right={60}
            shadow-camera-top={60}
            shadow-camera-bottom={-60}
          />
          <hemisphereLight args={[isBeach ? "#ffdc9a" : isSnow ? "#f0f8ff" : isRooftop ? "#ffa060" : "#88b8ff", isBeach ? "#b88a4a" : isSnow ? "#5a6878" : isRooftop ? "#3a2020" : "#3a3f47", 0.4]} />
          {!isSnow && !isBeach && <OutdoorSky sunset={isRooftop} />}
          {isBeach && <OutdoorSky sunset={true} />}
        </>
      ) : isHighway ? (
        <>
          <ambientLight intensity={0.9} color="#b0c0d8" />
          <hemisphereLight args={["#4a5878", "#141820", 0.6]} />
          <directionalLight position={[0, 25, 0]} intensity={0.6} color="#c0d0e8" />
        </>
      ) : isTunnels ? (
        <>
          <ambientLight intensity={0.75} color="#a8b0c0" />
          <hemisphereLight args={["#5a6478", "#1c1e26", 0.55]} />
          <directionalLight position={[0, 22, 0]} intensity={0.85} color="#c0c8d8" />
          <PlayerFlashlight state={state} />
        </>
      ) : isNeonRain ? (
        <>
          {/* Neon night: magenta ambient + cyan rim light + point lamps */}
          <ambientLight intensity={0.55} color="#a58fd8" />
          <hemisphereLight args={["#c07ad0", "#1a0a2e", 0.6]} />
          <directionalLight position={[10, 20, 5]} intensity={0.6} color="#F0ABFC" />
          <pointLight position={[-12, 8, 8]} intensity={1.4} color="#00F0FF" distance={38} decay={2} />
          <pointLight position={[12, 8, -8]} intensity={1.4} color="#F0ABFC" distance={38} decay={2} />
        </>
      ) : (
        <>
          <ambientLight intensity={1.5} color="#c8d0e0" />
          <hemisphereLight args={["#8ea0d0", "#3a3f4f", 1.2]} />
          <directionalLight position={[0, 20, 0]} intensity={1.1} color="#d8e0f0" />
          <Ceiling state={state} />
          <PlayerFlashlight state={state} />
        </>
      )}

      <Ground level={state.level} />
      <ParkingFloor state={state} />

      <Walls walls={state.walls} level={state.level} />

      {state.obstacles.map((o, i) => (
        <CarMesh key={`o-${i}`} {...o} />
      ))}
      {state.enemies.map((e, i) => (
        <CarMesh key={`e-${i}`} x={e.x} y={e.y} w={e.w} h={e.h} color={e.buggy ? "#FF8A00" : "#FF0055"} angle={e.angle} isEnemy />
      ))}
      {state.powers.map((pw, i) => (
        <PowerOrb key={`p-${i}`} pw={pw} t={state.time} />
      ))}
      {state.exits.map((e, i) => (
        <ExitBlock key={`ex-${i}`} e={e} />
      ))}

      <Fugitive state={state} />
      <GhostPlayer3D state={state} />
      <ChaseCamera state={state} />
    </>
  );
}
